import type { ReactNode } from 'react'
import { BrandMark } from '@/components/common/BrandMark'

/**
 * The signed-out screen: form on the left, a panel of colour on the right.
 *
 * Deliberately the same shape as Hub's `templates/auth/_split.php` — 62/38,
 * brand top-left, copyright bottom-left, decoration dropped below `lg` — so the
 * two apps feel like one product at the only screen somebody sees before they
 * are signed in. The colour is the difference: Services is green where Hub is
 * blue, which is how you tell at a glance which one you are signing into.
 *
 * Shared by sign-in, the reset request and the reset itself so the three cannot
 * drift apart, and so the decorative SVG exists once rather than three times.
 *
 * Colours come from the semantic tokens rather than fixed greys: unlike Hub,
 * Services follows the device's light/dark setting, and hardcoding zinc here
 * would give a dark form on a white page.
 */
export function AuthSplit({
  heading,
  lead,
  children,
}: {
  heading: string
  lead?: string
  children: ReactNode
}) {
  return (
    <div className="flex min-h-screen bg-background">
      {/* Form */}
      <div className="flex w-full flex-col px-6 py-8 sm:px-10 lg:w-[62%] lg:px-16">
        <div className="flex items-center gap-2.5 self-start">
          <BrandMark size="sm" />
          <span className="text-[15px] font-semibold tracking-tight">MDV Services</span>
        </div>

        {/*
          justify-center, not just items-center: on a wide monitor this column is
          over a thousand pixels and a left-aligned form sits in the corner with a
          void beside it. Centring also fixes the phone case, where the panel is
          hidden and the form would otherwise hug the edge.
        */}
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm py-10">
            <h1 className="text-3xl font-semibold tracking-tight">{heading}</h1>
            {lead ? <p className="mt-2 text-sm text-muted-foreground">{lead}</p> : null}
            <div className="mt-6">{children}</div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground/70">
          © {new Date().getFullYear()} Manantial De Vida USA, Inc
        </p>
      </div>

      {/*
        aria-hidden and dropped below lg. It carries no information, and on a
        phone a full-height graphic would push the fields below the fold.
      */}
      <div className="relative hidden overflow-hidden bg-green-600 lg:block lg:w-[38%]" aria-hidden="true">
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 400 900"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/*
              green-600 -> emerald-700. Hub shifts sky-600 -> blue-600 across the
              same gradient; this is the equivalent step in green, so the two
              panels have the same depth rather than one reading flat.
            */}
            <linearGradient id="authGradientServices" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#16a34a" />
              <stop offset="1" stopColor="#047857" />
            </linearGradient>
          </defs>
          <rect width="400" height="900" fill="url(#authGradientServices)" />
          <path d="M400 0v900H150C260 700 120 520 240 330 320 200 300 90 400 0Z" fill="#ffffff" opacity=".07" />
          <path d="M400 90v810H230C330 720 200 560 305 385 375 268 340 170 400 90Z" fill="#ffffff" opacity=".07" />
          <path d="M400 200v700H310C385 760 300 610 360 470 400 375 380 265 400 200Z" fill="#ffffff" opacity=".07" />
          <circle cx="330" cy="700" r="150" fill="#ffffff" opacity=".05" />
        </svg>
      </div>
    </div>
  )
}
